'use client';

import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import Image from 'next/image';
import { SignedIn, SignedOut, UserButton, SignInButton } from '@clerk/nextjs';
import { useAuth } from '@/lib/clerk/compat-client';

const showcaseLinks = [
  { name: 'Products', href: '/products' },
  { name: 'Case Studies', href: '/case-studies' },
  { name: 'Blog', href: '/blog' },
];

const aboutUsLinks = [
  { name: 'About Data Jukebox', href: '/about' },
  { name: 'DJ4Good', href: '/dj4good' },
  { name: 'Testimonials', href: '/testimonials' },
];

const navLinks = [
  { name: 'Home', href: '/' },
  { name: 'Benchmark', href: '/benchmark' },
  { name: 'Contact', href: '/contact' },
];

interface DropdownProps {
  title: string;
  links: { name: string; href: string }[];
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  buttonRef: React.RefObject<HTMLDivElement>;
}

const Dropdown = ({ title, links, isOpen, setIsOpen, buttonRef }: DropdownProps) => (
  <div className="relative" ref={buttonRef}>
    <button
      type="button"
      onClick={() => setIsOpen(!isOpen)}
      className="text-gray-600 hover:text-[#59b9c1] font-medium flex items-center focus:outline-none"
    >
      {title}
      <svg
        className={`w-4 h-4 ml-1 transition-transform duration-200 ${isOpen ? 'transform rotate-180' : ''}`}
        fill="none" stroke="currentColor" viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    {isOpen && (
      <div className="absolute left-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50">
        {links.map(link => (
          <Link
            key={link.name}
            href={link.href}
            className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
            onClick={() => setIsOpen(false)}
          >
            {link.name}
          </Link>
        ))}
      </div>
    )}
  </div>
);


const Header = () => {
  const { isLoaded } = useAuth();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isShowcaseOpen, setIsShowcaseOpen] = useState(false);
  const [isAboutUsOpen, setIsAboutUsOpen] = useState(false);
  const showcaseRef = useRef<HTMLDivElement>(null);
  const aboutUsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (showcaseRef.current && !showcaseRef.current.contains(event.target as Node)) {
        setIsShowcaseOpen(false);
      }
      if (aboutUsRef.current && !aboutUsRef.current.contains(event.target as Node)) {
        setIsAboutUsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header className="bg-white shadow-sm sticky top-0 z-50">
      <nav className="container mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center h-22">
        <Link href="/" className="flex-shrink-0">
          <Image src="/images/DJLogo2025.svg" alt="Data Jukebox Logo" width={161} height={64} priority />
        </Link>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
          <Link href="/" className="text-gray-600 hover:text-[#59b9c1] font-medium">Home</Link>
          <Dropdown title="Showcase" links={showcaseLinks} isOpen={isShowcaseOpen} setIsOpen={setIsShowcaseOpen} buttonRef={showcaseRef} />
          <Dropdown title="About Us" links={aboutUsLinks} isOpen={isAboutUsOpen} setIsOpen={setIsAboutUsOpen} buttonRef={aboutUsRef} />
          {navLinks.filter(link => link.name !== 'Home').map(link => (
            <Link key={link.name} href={link.href} className="text-gray-600 hover:text-[#59b9c1] font-medium">{link.name}</Link>
          ))}
          <div className="ml-4">
            {isLoaded && (
              <>
                <SignedIn>
                  <UserButton afterSignOutUrl="/" />
                </SignedIn>
                <SignedOut>
                  <SignInButton mode="modal">
                    <button className="text-gray-600 hover:text-[#59b9c1] font-medium">Log In</button>
                  </SignInButton>
                </SignedOut>
              </>
            )}
          </div>
        </div>

        {/* Mobile menu button */}
        <div className="md:hidden">
          <button type="button" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-gray-600 hover:text-[#59b9c1] focus:outline-none">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {isMobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-white border-t border-gray-200">
          <div className="px-2 pt-2 pb-3 space-y-1">
            <Link href="/" className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-[#59b9c1]" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
            <div className="px-3 py-2 text-base font-medium text-gray-800">Showcase</div>
            {showcaseLinks.map(link => (
              <Link key={link.name} href={link.href} className="block pl-6 pr-3 py-2 text-base font-medium text-gray-600 hover:text-[#59b9c1]" onClick={() => setIsMobileMenuOpen(false)}>{link.name}</Link>
            ))}
            <div className="px-3 py-2 text-base font-medium text-gray-800">About Us</div>
            {aboutUsLinks.map(link => (
              <Link key={link.name} href={link.href} className="block pl-6 pr-3 py-2 text-base font-medium text-gray-600 hover:text-[#59b9c1]" onClick={() => setIsMobileMenuOpen(false)}>{link.name}</Link>
            ))}
            {navLinks.map(link => (
              <Link key={link.name} href={link.href} className="block px-3 py-2 text-base font-medium text-gray-600 hover:text-[#59b9c1]" onClick={() => setIsMobileMenuOpen(false)}>{link.name}</Link>
            ))}
            <div className="border-t border-gray-200 mt-2 pt-2">
              {isLoaded && (
                <>
                  <SignedOut>
                    <SignInButton mode="modal">
                      <button className="w-full text-left block px-3 py-2 text-base font-medium text-gray-600 hover:text-[#59b9c1]">Log In</button>
                    </SignInButton>
                  </SignedOut>
                  <SignedIn>
                    <div className="px-3 py-2">
                      <UserButton afterSignOutUrl="/" />
                    </div>
                  </SignedIn>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;